import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ConfirmationDialog from './ConfirmationDialog';

export type ConfirmOptions = {
  title?: string;
  message?: string;
  details?: Record<string, unknown> | string | null;
  warningMessage?: string | null;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'default' | 'danger' | 'secondary' | 'accent' | 'warning' | 'info';
  zIndex?: number;
};

type ConfirmFn = (options?: ConfirmOptions) => Promise<boolean>;

type PendingConfirm = ConfirmOptions & {
  resolve: (value: boolean) => void;
};

const ConfirmContext = createContext<ConfirmFn | null>(null);

const DEFAULTS: Required<
  Pick<ConfirmOptions, 'title' | 'message' | 'confirmText' | 'cancelText' | 'confirmVariant'>
> = {
  title: 'Confirm Action',
  message: 'Are you sure you want to proceed?',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  confirmVariant: 'danger',
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);

  const close = useCallback((value: boolean) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(value);
  }, []);

  const confirm = useCallback<ConfirmFn>((options = {}) => {
    return new Promise<boolean>((resolve) => {
      // If a dialog is already open, treat the previous waiter as cancelled.
      if (pendingRef.current) {
        pendingRef.current.resolve(false);
      }
      const next: PendingConfirm = { ...options, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmationDialog
        isOpen={Boolean(pending)}
        onClose={() => close(false)}
        onConfirm={() => close(true)}
        title={pending?.title ?? DEFAULTS.title}
        message={pending?.message ?? DEFAULTS.message}
        details={pending?.details ?? null}
        warningMessage={pending?.warningMessage ?? null}
        confirmText={pending?.confirmText ?? DEFAULTS.confirmText}
        cancelText={pending?.cancelText ?? DEFAULTS.cancelText}
        confirmVariant={pending?.confirmVariant ?? DEFAULTS.confirmVariant}
        zIndex={pending?.zIndex}
      />
    </ConfirmContext.Provider>
  );
};

/** Promise-based confirm dialog for use anywhere under ConfirmProvider. */
export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return confirm;
}
